<template>
	<div class="section">
		<div class="container">
			<div class="columns">
				<div id="searchBar" class="column" v-if="query">
					<b-field label="Keyword search" label-position="on-border">
						<b-input
							size="is-medium"
							placeholder="Keyword Search (AND, OR, -/NOT)"
							v-model="query.Keywords"></b-input>
					</b-field>

					<b-field label="Title" label-position="on-border" grouped group-multiline>
						<b-input size="is-medium" expanded placeholder="Title" v-model="query.Title"></b-input>
						<div class="help">
							<b-checkbox v-model="query.TitleSpan.Current">Current</b-checkbox>
							<br />
							<b-checkbox v-model="query.TitleSpan.Past">Past</b-checkbox>
						</div>
					</b-field>

					<b-field label="Company" label-position="on-border" grouped group-multiline>
						<b-input
							size="is-medium"
							expanded
							placeholder="Company"
							v-model="query.Company"></b-input>
						<div class="help">
							<b-checkbox v-model="query.CompanySpan.Current">Current</b-checkbox>
							<br />
							<b-checkbox v-model="query.CompanySpan.Past">Past</b-checkbox>
						</div>
					</b-field>

					<b-field label="Name" label-position="on-border">
						<b-input size="is-medium" v-model="query.Name" placeholder="Name"></b-input>
					</b-field>

					<b-field label="Education / Degrees" label-position="on-border">
						<b-input
							size="is-medium"
							placeholder="Education / Degrees"
							v-model="query.Education"></b-input>
					</b-field>

					<b-field label="Resume search" label-position="on-border">
						<b-input
							v-model="query.Resume"
							size="is-medium"
							type="textarea"
							placeholder="Resume search (includes both exact terms and similar concepts"></b-input>
					</b-field>
					<b-field>
						<b-checkbox v-model="query.HasResume">only profiles with resume</b-checkbox>
					</b-field>
					<button
						id="goButton"
						class="button is-primary"
						v-bind:class="searching ? 'is-loading' : ''"
						@click="search">
						Search
					</button>
					<button style="margin-left: 15px" class="button is-warning" @click="resetQuery">
						Reset Search
					</button>
				</div>

				<div class="column">
					<b-field label="Industry" label-position="on-border" grouped group-multiline>
						<b-taginput
							size="is-medium"
							expanded
							v-model="tags.Industry.selected"
							:data="tags.Industry.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Industry"
							@typing="getFiltered('Industry', $event)"></b-taginput>
						<div class="help">
							<b-checkbox v-model="query.IndustrySpan.Current" native-value="true">
								Current
							</b-checkbox>
							<br />
							<b-checkbox v-model="query.IndustrySpan.Past" native-value="true">Past</b-checkbox>
							<br />
							<b-checkbox v-model="query.IndustrySpan.Seeking" native-value="true">
								Seeking
							</b-checkbox>
						</div>
					</b-field>

					<b-field label="Function" label-position="on-border" grouped group-multiline>
						<b-taginput
							size="is-medium"
							expanded
							v-model="tags.Function.selected"
							:data="tags.Function.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Function"
							@typing="getFiltered('Function', $event)"></b-taginput>
						<div class="help">
							<b-checkbox v-model="query.FunctionSpan.Current" native-value="true">
								Current
							</b-checkbox>
							<br />
							<b-checkbox v-model="query.FunctionSpan.Past" native-value="true">Past</b-checkbox>
							<br />
							<b-checkbox v-model="query.FunctionSpan.Seeking" native-value="true">
								Seeking
							</b-checkbox>
						</div>
					</b-field>

					<b-field label="Region" label-position="on-border">
						<b-taginput
							size="is-medium"
							v-model="tags.Region.selected"
							:data="tags.Region.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Region"
							@typing="getFiltered('Region', $event)"></b-taginput>
					</b-field>

					<b-field label="Country" label-position="on-border" grouped group-multiline>
						<b-taginput
							size="is-medium"
							expanded
							v-model="tags.Country.selected"
							:data="tags.Country.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Country"
							@typing="getFiltered('Country', $event)"></b-taginput>
						<div class="help">
							<b-checkbox v-model="query.CountrySpan.Current" native-value="true">
								Current
							</b-checkbox>
							<br />
							<b-checkbox v-model="query.CountrySpan.Past" native-value="true">Past</b-checkbox>
							<br />
							<b-checkbox v-model="query.CountrySpan.Seeking" native-value="true">
								Seeking
							</b-checkbox>
						</div>
					</b-field>

					<b-field label="State" label-position="on-border" grouped group-multiline>
						<b-taginput
							size="is-medium"
							expanded
							v-model="tags.State.selected"
							:data="tags.State.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="State"
							@typing="getFiltered('State', $event)"></b-taginput>
						<div class="help">
							<b-checkbox v-model="query.StateSpan.Current" native-value="true">Current</b-checkbox>
							<br />
							<b-checkbox v-model="query.StateSpan.Seeking" native-value="true">Seeking</b-checkbox>
						</div>
					</b-field>

					<b-field label="Expected Compensation" label-position="on-border">
						<b-taginput
							size="is-medium"
							v-model="tags.Compensation.selected"
							:data="tags.Compensation.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Compensation"
							@typing="getFiltered('Compensation', $event)">
							<template v-slot="props">
								<span v-html="$currency.range(props.option.Name)"></span>
							</template>
						</b-taginput>
					</b-field>

					<b-field label="Language" label-position="on-border">
						<b-taginput
							size="is-medium"
							v-model="tags.Language.selected"
							:data="tags.Language.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Language"
							@typing="getFiltered('Language', $event)"></b-taginput>
					</b-field>

					<b-field label-position="on-border" class="label-tooltip">
						<template #label>
							Source
							<b-tooltip>
								<b-icon icon="question-circle"></b-icon>
								<template v-slot:content>
									<b-icon icon="female"></b-icon>
									Woman Board Candidate
								</template>
							</b-tooltip>
						</template>
						<b-taginput
							size="is-medium"
							v-model="tags.Source.selected"
							:data="tags.Source.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Source"
							@typing="getFiltered('Source', $event)">
							<template v-slot="props">
								{{ props.option.Name }}
								<b-icon v-if="props.option.IsWomanBoardCandidate" icon="female"></b-icon>
							</template>
						</b-taginput>
					</b-field>

					<b-field label="Personal title" style="margin: 1.5rem 0" label-position="on-border">
						<b-taginput
							size="is-medium"
							v-model="tags.Gender.selected"
							:data="tags.Gender.filtered"
							autocomplete
							:allow-new="false"
							:open-on-focus="true"
							field="Name"
							placeholder="Gender"
							@typing="getFiltered('Gender', $event)"></b-taginput>
					</b-field>

					<b-field label="Last login within" label-position="on-border">
						<b-select v-model="query.LastLogin" size="is-medium" expanded>
							<option value="">any time</option>
							<option value="90d">last 90 days</option>
							<option value="6m">last 6 months</option>
							<option value="1y">last 1 year</option>
							<option value="2y">last 2 years</option>
						</b-select>
					</b-field>
				</div>
			</div>
			<Disclaimer />
		</div>
	</div>
</template>

<script>
	import Disclaimer from "@/components/other/disclaimer.vue";

	var pressEnter = null;
	export default {
		name: "app",
		components: {
			Disclaimer
		},
		data() {
			return {
				users: [],
				industryTest: [],
				query: {},
				tags: {
					Industry: { filtered: [], selected: [] },
					Function: { filtered: [], selected: [] },
					Region: { filtered: [], selected: [] },
					Country: { filtered: [], selected: [] },
					State: { filtered: [], selected: [] },
					Compensation: { filtered: [], selected: [] },
					Language: { filtered: [], selected: [] },
					Gender: { filtered: [], selected: [] },
					Source: { filtered: [], selected: [] }
				},
				searching: false,
				Industries: [],
				JobFunctions: {},
				Regions: {},
				Countries: {},
				States: {},
				CompensationRanges: {},
				Languages: {},
				PersonalTitles: {},
				previous: false
			};
		},
		watch: {
			tags: {
				deep: true,
				handler(tags) {
					const noadd = [-2]; //Ids to reject
					for (var tag in tags) {
						if (tags[tag].selected) {
							this.query[tag] = tags[tag].selected
								.filter((x) => !noadd.some((id) => id == x.Id))
								.map((x) => x.Id);

							if (tag === "Source") {
								if (tags[tag].selected.some((x) => x.Id === -2)) {
									tags[tag].data
										.filter((x) => x.IsWomanBoardCandidate)
										.forEach((x) => {
											this.query[tag].push(x.Id);
										});
								}
							}
						}
					}
				}
			}
		},
		async created() {
			pressEnter = document.addEventListener("keydown", this.searchOnEnter);
		},
		async mounted() {
			this.getProfileCount();
			await this.getFilters();
		},
		methods: {
			async getFilters() {
				try {
					var response = await this.$http.get("api/filter/all");

					this.tags.Industry.data = response.data.Industries;
					this.tags.Industry.filtered = this.tags.Industry.data;

					this.tags.Function.data = response.data.JobFunctions;
					this.tags.Function.filtered = this.tags.Function.data;

					this.tags.Region.data = response.data.Regions;
					this.tags.Region.filtered = this.tags.Region.data;

					this.tags.Country.data = response.data.Countries;
					this.tags.Country.filtered = this.tags.Country.data;

					this.tags.State.data = response.data.States;
					this.tags.State.filtered = this.tags.State.data;

					this.tags.Compensation.data = response.data.CompensationRanges;
					this.tags.Compensation.filtered = this.tags.Compensation.data;

					this.tags.Language.data = response.data.Languages;
					this.tags.Language.filtered = this.tags.Language.data;

					this.tags.Gender.data = response.data.PersonalTitles;
					this.tags.Gender.filtered = this.tags.Gender.data;

					this.Languages = response.data.Languages;
					this.PersonalTitles = response.data.PersonalTitles;

					response.data.Sources = [{ Id: -2, Name: "<Select All Woman Board Candidates>" }].concat(
						response.data.Sources
					);
					this.tags.Source.data = response.data.Sources;
					this.tags.Source.filtered = this.tags.Source.data;

					this.query = this.$store.getters.emptyQuery;

					this.query = this.$store.getters.emptyQuery;

					if (this.$store.getters.query != null) {
						this.query = this.$store.getters.query;

						for (var q in this.query) {
							if (this.query[q] && this.query[q].length > 0 && this.tags[q])
								this.tags[q].selected = this.tags[q].data.filter((x) =>
									this.query[q].some((y) => y == x.Id)
								);
						}
					}
				} catch (err) {
					console.error(err);
				}
			},
			getFiltered: function (tag, text) {
				this.tags[tag].filtered = this.tags[tag].data.filter((option) =>
					text == "" ? true : option.Name.toLowerCase().indexOf(text.toLowerCase()) >= 0
				);
			},
			search: async function () {
				if (this.query) {
					sessionStorage.removeItem("BSDBResults");
					this.$store.dispatch("setQuery", this.query);
					this.$store.dispatch("setQueryId", null);
					this.$router.push("/results");
				}
			},
			searchOnEnter(e) {
				if (this.$route.name == "Home" && e.keyCode == 13) {
					sessionStorage.removeItem("BSDBResults");
					this.search();
				}
			},
			resetQuery() {
				this.$store.dispatch("setQuery", JSON.parse(JSON.stringify(this.$emptyQuery)));
				this.$store.dispatch("setQueryId", null);
				this.$set(this, "query", JSON.parse(JSON.stringify(this.$emptyQuery)));
				this.tags.Industry.selected = [];
				this.tags.Function.selected = [];
				this.tags.Region.selected = [];
				this.tags.Country.selected = [];
				this.tags.State.selected = [];
				this.tags.Compensation.selected = [];
				this.tags.Language.selected = [];
				this.tags.Gender.selected = [];
				this.tags.Source.selected = [];
			},
			getProfileCount() {
				this.$http.get("/api/profile/total").then((resp) => {
					if (resp.data && resp.data >= 0) {
						this.$store.dispatch("setTotal", resp.data);
					}
				});
			}
		},
		beforeDestroy() {
			document.removeEventListener("keyup", this.searchOnEnter);
		}
	};
</script>

<style>
	.home {
		padding-left: 10%;
		padding-right: 10%;
	}

	.field:not(:last-child) {
		margin: 1.5rem 0;
	}

	.row:after {
		content: "";
		display: table;
		clear: both;
	}

	.border-bottom {
		border-bottom: solid 1px #eee;
	}

	.filterIndent {
		padding-left: 2%;
	}
</style>

